import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { AspectRatioWrapper, IconSpinner, NamedLink, ResponsiveImage } from '..';
import Icons from '../Icons/Icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { richText } from '../../util/richText';
import { SUBSCRIPTION_TYPES } from '../../util/enums';

import css from './BrandListingCard.module.css';
import { getUserSubscription } from '../../util/userDataExtraction';
import { formatPrimaryIndustry, isArrayLength } from '../../util/genericHelpers';
import {
  deleteItemFromFavorites,
  favoritesSelector,
} from '../../containers/FavoritesPage/FavoritesPage.duck';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../ducks/user.duck';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

export const BrandListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    isLandingListingCard,
    isSearchPage = false,
    isBrandListings,
    openFavoritesModal,
    setFirstImage,
    hideFavorite,
  } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const currentUser = useSelector(userSelector);
  const { favorites = [], removedItemId } = useSelector(favoritesSelector);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUserId = currentUser?.id?.uuid;
  const listingId = listing?.id?.uuid;

  const favoritesList = favorites || [];

  const isOwnListing = currentUserId && listing?.author?.id?.uuid === currentUserId;
  const isAlreadyAddedToFavoriteslist =
    isArrayLength(favoritesList) &&
    favoritesList.some(category => category.ids?.includes(listingId));
  const classes = classNames(rootClassName || css.root, className, css.brandListingCard, {
    [css.landingListingCard]: isLandingListingCard,
    [css.landingBrandListingCard]: isBrandListings,
  });

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description, publicData } = currentListing.attributes || {};

  const { pub_primary_industry, pub_job_title, city = '', state = '' } = publicData || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'scaled-medium',
  } = config.layout.listingImage;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const navigateToListing = () => {
    history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {}));
  };

  const brandPremierMaybe = Boolean(
    getUserSubscription(listing?.author) === SUBSCRIPTION_TYPES.BRAND_PREMIER
  );

  const handleFavoritesList = async e => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isAlreadyAddedToFavoriteslist) {
        await dispatch(
          deleteItemFromFavorites({
            isAuthenticated,
            id: listingId,
            dispatch,
            favorites,
            config,
            isAuthenticated,
            userId: currentUserId,
          })
        );
      } else {
        openFavoritesModal(listingId);
        setFirstImage(firstImage);
      }
    } catch (error) {
      console.log('Error while updating favorites');
    }
  };
  return (
    <>
      <div className={classes} onClick={navigateToListing}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
          {!isOwnListing && !hideFavorite && (
            <span
              className={classNames(css.favorite, isAlreadyAddedToFavoriteslist && css.favorited)}
              onClick={e => {
                e.stopPropagation();
                isAuthenticated
                  ? handleFavoritesList(e)
                  : history.push(
                      createResourceLocatorString('SignupPage', routeConfiguration, {}, {})
                    );
              }}
            >
              {removedItemId === listingId ? <IconSpinner /> : <Icons name="star" />}
            </span>
          )}
        </AspectRatioWrapper>

        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.cardTitle}>
              <h2>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </h2>
              <span className={css.brandPremier}>
                <Icons name="verify" /> <FormattedMessage id="BrandListingCard.verified" />
                {brandPremierMaybe
                  ? SUBSCRIPTION_TYPES.BRAND_PREMIER
                  : SUBSCRIPTION_TYPES.BRAND_STANDARD}
              </span>
            </div>
            <>
              {description && (
                <p
                  className={classNames({
                    [css.cardDescriptionLanding]: isLandingListingCard,
                    [css.cardDescription]: !isLandingListingCard,
                  })}
                >
                  {description}
                </p>
              )}
              {pub_primary_industry && (
                <p
                  className={classNames({
                    [css.contactLanding]: isLandingListingCard,
                    [css.contactInfo]: !isLandingListingCard,
                  })}
                >
                  <strong>
                    <FormattedMessage id="BrandListingCard.industry" />
                  </strong>{' '}
                  {formatPrimaryIndustry(pub_primary_industry)}
                </p>
              )}

              {(city || state) && (
                <p
                  className={classNames({
                    [css.contactLanding]: isLandingListingCard,
                    [css.contactInfo]: !isLandingListingCard,
                  })}
                >
                  <strong>
                    <FormattedMessage id="BrandListingCard.location" />
                  </strong>{' '}
                  {city && state ? `${city}, ${state}` : city || state}
                </p>
              )}
              {/* {pub_job_title && (
                  <p className={css.contactInfo}>
                    <strong>
                      <FormattedMessage id="BrandListingCard.jobTitle" />
                    </strong>{' '}
                    {pub_job_title}
                  </p>
                )} */}
            </>
          </div>

          <NamedLink className={css.findLink} name="LandingPage">
            <FormattedMessage id="BrandListingCard.explore" />
            <Icons name="rightArrow" />{' '}
          </NamedLink>
        </div>
      </div>
    </>
  );
};

BrandListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

BrandListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(BrandListingCardComponent);
